<template>

  <div class="optionPanel" >
    <div class="optionPanelCard">
      <div class="titleFileLine">
        <div class="leftBlock"><w-icon color="light2" icon="file-document-check-outline" size="large"/> </div>
        <div class="rightBlock">
          <div class="fileLoaded">{{$t('aiAvatar.optionPanel.generationMode.fileLoaded')}}</div>
          <div class="fileName">{{fileName}}</div>
        </div>
      </div>
      <div class="closeButton" @click="close()">
        <w-icon icon="close" size="small"/>
      </div>
      <!-- SEPERATOR -->
      <div class="hr"></div>

      <!-- LANGUAGE + GENERATION MODE -->
      <div class="optionLine">
        <!-- LANGUAGE -->
        <div class="leftBlock">
          <div class="labelOption">
            {{$t('aiAvatar.optionPanel.generationMode.generationMode')}}
          </div>
          <div class="contentOption dropdownGenerationMode">
          <b-dropdown v-model="generationMode" expanded @input="onGenerationModeChange">
            <template #trigger>
              <div class="fakeSelectBox">{{getLabelForGenerationMode(generationMode) }}</div>
            </template>

            <!-- ENHANCED -->
            <b-dropdown-item :value="SlideGenerationModeEnum.ENHANCED" aria-role="listitem">
                <div class="dropDownItemLabel">
                  {{getLabelForGenerationMode(SlideGenerationModeEnum.ENHANCED)}}
                </div>
                <div class="dropDownItemHelpLabel">
                  {{getLabelHelpForGenerationMode(SlideGenerationModeEnum.ENHANCED)}}
                </div>
                <div class="isSelected">
                  <w-icon icon="check" size="default" color="light2"/>
                </div>
            </b-dropdown-item>
            <!-- STRICT -->
            <b-dropdown-item :value="SlideGenerationModeEnum.STRICT" aria-role="listitem">
              <div class="dropDownItemLabel">
                {{getLabelForGenerationMode(SlideGenerationModeEnum.STRICT)}}
              </div>
              <div class="dropDownItemHelpLabel">
                {{getLabelHelpForGenerationMode(SlideGenerationModeEnum.STRICT)}}
              </div>
              <div class="isSelected">
                <w-icon icon="check" size="default" color="light2"/>
              </div>
            </b-dropdown-item>
            <!-- NONE -->
            <b-dropdown-item :value="SlideGenerationModeEnum.NONE" aria-role="listitem">
              <div class="dropDownItemLabel">
                {{getLabelForGenerationMode(SlideGenerationModeEnum.NONE)}}
              </div>
              <div class="dropDownItemHelpLabel">
                {{getLabelHelpForGenerationMode(SlideGenerationModeEnum.NONE)}}
              </div>
              <div class="isSelected">
                <w-icon icon="check" size="default" color="light2"/>
              </div>
            </b-dropdown-item>
          </b-dropdown>
          </div>
        </div>
      <!-- GENERATION MODE -->
      <div class="rightBlock">
        <div class="labelOption">
          {{$t('aiAvatar.optionPanel.generationMode.language')}}
        </div>
        <div class="contentOption">
          <w-select size="small" @input="onAvatarLangChange" :value="langSelected">
            <option :value="language.value" v-for="language in languageList" :key="language.value+'listOfLanguageGeneration'">
              {{language.label}}
            </option>
          </w-select>
        </div>
        </div>
      </div>

      <div class="optionLine vertical">
        <div class="labelOption">
          {{$t('aiAvatar.optionPanel.generationMode.customInstruction')}}
        </div>
        <div class="contentOption">
          <w-input type="textarea" class="customInstuctionArea" @input="onCustomInstructionChange"
                   :placeholder="$t('aiAvatar.optionPanel.generationMode.customInstructionPlaceholder')"/>
        </div>
      </div>

      <div class="buttonValidate">
        <w-button icon="auto-fix" @click="generateLLM">{{$t('aiAvatar.optionPanel.generationMode.buttons.generate')}}</w-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import WSelect from "@/components/wrapper/w-select.vue";
import WInput from "@/components/wrapper/w-input.vue";
import WIcon from "@/components/wrapper/w-icon.vue";
import WButton from "@/components/wrapper/w-button.vue";
import { Slide, SlideGenerationMode } from "@/store/slides2Video/slides2VideoModels";
import { listOfBaseLanguages } from "@/utils/voiceUtil";
import store from "@/store";
import { SELECT_AI_LANG } from "@/store/AIAvatar/AIAVatarAction";
import { SELECT_CUSTOM_INSTRUCTION, SELECT_GENERATION_MODE } from "@/store/slides2Video/slides2VideoAction";
import { softmax } from "@tensorflow/tfjs";

@Component({
  components: { WButton, WIcon, WInput, WSelect },
  computed: {},
})
export default class AIOptionPanel extends Vue {

    @Prop({required: true})
    private fileName!: string;

    private generationMode: SlideGenerationMode=SlideGenerationMode.ENHANCED;


    get isSlideInProgress(){
      return store.getters.isProceedSlideInProgress
    }

    get SlideGenerationModeEnum(){
      return SlideGenerationMode;
    }
    get languageList(){
      const autoDetectOption = {label: this.$t('generic.autodetect'), value: null};
      return [autoDetectOption, ...listOfBaseLanguages];
    }
    get langSelected() {
      return store.getters.getAvatarLangSelected || this.languageList[0].value
    }

    private getLabelForGenerationMode(generationMode: SlideGenerationMode){
      switch (generationMode) {
        case SlideGenerationMode.ENHANCED:
          return this.$t('aiAvatar.optionPanel.generationMode.enhanced');
        case SlideGenerationMode.STRICT:
          return this.$t('aiAvatar.optionPanel.generationMode.strict');
        case SlideGenerationMode.NONE:
          return this.$t('aiAvatar.optionPanel.generationMode.none');
        default:
          return "";
      }
    }
  private getLabelHelpForGenerationMode(generationMode: SlideGenerationMode){
    switch (generationMode) {
      case SlideGenerationMode.ENHANCED:
        return this.$t('aiAvatar.optionPanel.generationMode.enhancedHelp');
      case SlideGenerationMode.STRICT:
        return this.$t('aiAvatar.optionPanel.generationMode.strictHelp');
      case SlideGenerationMode.NONE:
        return this.$t('aiAvatar.optionPanel.generationMode.noneHelp');
      default:
        return "";
    }
  }

  private onAvatarLangChange(lang) {
    store.dispatch(SELECT_AI_LANG, lang)
  }

  private onGenerationModeChange(generationMode: SlideGenerationMode) {
      store.dispatch(SELECT_GENERATION_MODE, generationMode)
  }

  private onCustomInstructionChange(value: string) {
    store.dispatch(SELECT_CUSTOM_INSTRUCTION, value)
  }

  private generateLLM(){
      this.$emit('validate')
  }
  private close(){
    this.$emit('close')
  }


}
</script>

<style lang="scss">
.customInstuctionArea {
  .textarea {
    min-height: 80px;
    height: 80px;
    //resize: none;
  }
  .textarea:not([rows]){
    min-height: 80px;
  }
}

</style>
<style lang="scss" scoped>

.optionPanel{
  // covers the whole recorder, like the video upload panel
  position: fixed;
  background: var(--light);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  padding: 16px;
  width: 100%;
  height: 100%;
  z-index: 40;

  // the options themselves stay in a white card
  .optionPanelCard {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 640px;
    max-height: 100%;
    overflow-y: auto;
    padding: 24px;
    background: var(--white);
    border-radius: 24px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  }

  .closeButton{
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
  }

  .titleFileLine{
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    .leftBlock{
      width: 64px;
    }
    .rightBlock{
      flex: 1;
      .fileLoaded{
        font-size: 12px;
        color: var(--light2);
      }
      .fileName{
        font-size: 16px;
        color: var(--black);
      }
    }
  }
  .optionLine{
    margin-top: 16px;
    display: flex;
    align-items: flex-start;
    text-align: left;
    width: 100%;

    .labelOption {
      margin-bottom: 4px;
    }

    // the select must line up with the fake select box of the other column
    .contentOption {
      width: 100%;

      ::v-deep .field {
        margin-bottom: 0;
      }

      ::v-deep .select,
      ::v-deep select {
        width: 100%;
        height: 30px;
        font-size: 14px;
      }
    }
    .leftBlock{
      width: 50%;
      text-align: left;
      padding-right: 8px;
    }
    .rightBlock{
      width: 50%;
      padding-left: 8px;
      text-align: left;
    }
    &.vertical{
      flex-direction: column;
    }

    .customInstuctionArea{
      height: 86px;
      width: 100%;
      textarea{
        width: 100%;
      }
    }
  }
  .hr{
    margin-top: 16px;
    margin-bottom: 16px;
    border: 1px solid var(--light1);
    width:100%;
  }

  .dropdownGenerationMode{
    width: 100%;
  }

  .fakeSelectBox {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border: 1px solid var(--light1);
    background: white;
    font-size: 14px;
    border-radius: 8px;
    padding: 4px;
    height: 30px;
    cursor: pointer;

    &::after {
      border: 3px solid var(--light2);
      border-radius: 2px;
      border-right: 0;
      border-top: 0;
      content: " ";
      right:16px;
      display: block;
      height: 0.625em;
      margin-top: -0.4375em;
      pointer-events: none;
      position: absolute;
      top: 50%;
      transform: rotate(-45deg);
      transform-origin: center;
      width: 0.625em;
    }
  }

  .dropdown-item{
    .isSelected{
      display: none;
    }
    &.is-active{
      padding-right: 40px;
      background: white;
      .isSelected{
        display: block;
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
      }
    }
    .dropDownItemLabel {
      font-size: 14px;
    }

    .dropDownItemHelpLabel {
      color: var(--light2);
      font-size: 12px;
      white-space: break-spaces;
    }
  }

  // in the flow of the card, it used to hang below the old full-size panel
  .buttonValidate{
    margin-top: 24px;
    align-self: flex-end;
  }

}


</style>
